import { ReactNode } from 'react';
import { User } from '../../../interfaces/backend/User';
import { CodeType } from '../CodeType';
import { BackendCodeSample, UserObjectStringMap } from './BackendCodeSample';
import Paragraph from 'antd/lib/typography/Paragraph';
import Text from 'antd/lib/typography/Text';
import CodeBlock from '../CodeBlock';
import { BuildCodeParams } from './CodeSample';

export default class BackendLaravelSample extends BackendCodeSample {
  name = 'Laravel';
  language = 'php';
  codeType = CodeType.BACKEND;
  tabIcon = '';
  commentChar = '//';

  buildCode({
    environment,
    notification,
    user,
    mergeTags
  }: BuildCodeParams): ReactNode {
    const composerInstall =
      'composer require notificationapi/notificationapi-laravel-server-sdk:@dev';

    const service = `'providers' => [
    // ...
    NotificationAPI\\NotificationApiServiceProvider::class,
]`;

    const env = `NOTIFICATION_API_KEY=${environment.clientId}
NOTIFICATION_API_SECRET=${environment.clientSecret}`;

    const gen = `php artisan make:notification MyNotification`;

    const notifClass = `class MyNotification extends Notification
{
    // ...

    public function via($notifiable)
    {
        return ['notification-api'];
    }

    public function toNotificationApi($notifiable) 
    {
        return [
            "notificationId" => "${notification.notificationId}",
            "user" => [
                "id" => $notifiable->getAttribute('id'),
                "email" => $notifiable->getAttribute('email'),
            ],
            "mergeTags" => [
                "userName" => auth()->user()->name
            ]
        ];
    }
}`;

    const laravelSend = `<?php

use App\\Models\\User;
use App\\Notifications\\MyNotification;

$user = new User();
$user->name = '${user.id}';
$user->id = '${user.id}';
$user->email = '${user.email}';

// Send the notification to the user
$user->notify(new MyNotification($user));`;

    const code = `$data = [
    "notificationId" => "${notification.notificationId}",
    "user" => [
        ${this.buildUserObjectString(user, '\n        ')}
    ]
];

$result = notification_api($data);
#or
$result = (new NotificationApiService)->send($data)`;

    return (
      <>
        <Paragraph style={{ paddingTop: 10 }}>
          To use the PHP NotificationAPI SDK, you must install it with Composer:
        </Paragraph>
        <CodeBlock language="bash" code={composerInstall} />
        <Paragraph>
          Register the NotificationApiServiceProvider with{' '}
          <Text code>config/app.php</Text>
        </Paragraph>
        <CodeBlock language="php" code={service} />
        <Paragraph>
          Add NotificationAPI keys to <Text code>.env</Text>
        </Paragraph>
        <CodeBlock language="text" code={env} />
        <Paragraph>Generate notification</Paragraph>
        <CodeBlock language="bash" code={gen} />
        <Paragraph>
          Update the <Text code>MyNotification</Text> class
        </Paragraph>
        <CodeBlock language="php" code={notifClass} />
        <Paragraph>
          Once the NotificationAPI SDK is installed you can use the following
          code to send your first notification via Laravel's notifications:
        </Paragraph>
        <CodeBlock language={this.language} code={laravelSend} />
        <Paragraph>
          Alternatively you can send a notification without using Laravel's
          notifications:
        </Paragraph>
        <CodeBlock language={this.language} code={code} />
      </>
    );
  }

  buildUserObject(user: User): UserObjectStringMap {
    return {
      id: `"id" => "${user.id}"`,
      email: `"email" => "${user.email}"`,
      number: {
        value: `"number" => "${user.number}"`,
        comment: 'Replace with your phone number'
      }
    };
  }
}
